import { useMemo, useState } from "react";
import { DateTime } from "luxon";
import theme from "../../../theme";
import {
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";
import { Box } from "../../../components/Box";
import { Button } from "../../../components/Button";
import { Card } from "../../../components/Card";
import { Flex } from "../../../components/Flex";
import { H3 } from "../../../components/Heading";
import { Paragraph } from "../../../components/Paragraph";
import { Image } from "../../../components/Image";
import { NewMonitoringRecordFrame } from "./NewMonitoringRecordFrame";
import { useCountries } from "../../../data/useCountries";
import { useEntityTypes } from "../../../data/useEntityTypes";
import { monitoringEventSummary } from "../../../utils/screeningChecks";
import { useScreeningProvider } from "../../../data/useScreeningProvider";
import { useCreateEntityMonitoring } from "../../../data/useCreateEntityMonitoring";
import { useCreateMonitoring } from "../../../data/useCreateMonitoring";
import { showError } from "../../../utils/error-handling";
import { Modal } from "../../../components/Modal";
import { capitalise } from "../../../utils/string";

const formatDate = (iso?: string | null) =>
  iso ? DateTime.fromISO(iso).toFormat("dd/MM/yyyy") : "-";

const SoftCard = ({ children, p = 3 }: { children: any; p?: any }) => (
  <Card
    p={p}
    style={{
      borderRadius: 14,
      border: `1px solid ${theme.colors.divider}`,
      background: theme.colors.white,
      boxShadow: "none",
    }}
  >
    {children}
  </Card>
);

const Muted = ({ children }: { children: any }) => (
  <Paragraph mb={0} color={theme.colors.muted}>
    {children}
  </Paragraph>
);

type LocationState = {
  monitoring?: {
    pep_and_sanction_required: boolean | string;
    adverse_media_required: boolean | string;
  };
  externalReference?: string | null;
  primaryDivision?: number | string;
  primaryTeam?: number | string;
};

const isYes = (v: any) => v === true || v === "true";

const toId = (v: any): number | null => {
  if (typeof v === "number") return v;
  if (v && typeof v === "object" && typeof v.id === "number") return v.id;
  return null;
};

const embeddedName = (v: any): string | null =>
  v && typeof v === "object" && typeof v.name === "string" ? v.name : null;

export const ProfileMonitoringSummaryStep = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const profileId = Number(id);

  const { profile } = useOutletContext<any>();
  const isEntity = profile?._type === "entity";
  const { isLexisNexis2 } = useScreeningProvider();

  const { monitoring, externalReference, primaryDivision, primaryTeam } =
    (location.state || {}) as LocationState;

  const { data: countries } = useCountries();
  const { data: entityTypes } = useEntityTypes();
  const [doubleClick, setDoubleClick] = useState(false);

  const { mutate: createEntityMonitoring } = useCreateEntityMonitoring();
  const { mutate: createMonitoring } = useCreateMonitoring();

  const displayName = useMemo(() => {
    if (isEntity) return profile?.name ?? "-";
    return (
      [profile?.first_name, profile?.middle_name, profile?.last_name]
        .filter(Boolean)
        .join(" ") || "-"
    );
  }, [isEntity, profile]);

  const countryObj = useMemo(() => {
    const list = Array.isArray(countries) ? countries : countries?.data ?? [];
    const countryId = toId(profile?.country);
    const found = list.find((c: any) => c.id === countryId);

    if (found) return found;
    if (embeddedName(profile?.country)) return profile.country;
    return null;
  }, [countries, profile?.country]);

  const entityTypeName = useMemo(() => {
    const list = Array.isArray(entityTypes)
      ? entityTypes
      : entityTypes?.data ?? [];
    const entityTypeId = toId(profile?.entity_type);
    return (
      list.find((x: any) => x.id === entityTypeId)?.name ??
      embeddedName(profile?.entity_type)
    );
  }, [entityTypes, profile?.entity_type]);

  const registrationNumber = profile?.registration_number || null;
  const incorporationDate = profile?.date_of_incorporation
    ? formatDate(profile.date_of_incorporation)
    : null;

  const dateOfBirth = profile?.date_of_birth
    ? formatDate(profile.date_of_birth)
    : null;
  const genderLabel = profile?.gender ? capitalise(profile.gender) : null;
  const email = profile?.email || null;

  const hasDetails = isEntity
    ? !!(entityTypeName || registrationNumber || incorporationDate || countryObj?.name)
    : !!(dateOfBirth || genderLabel || email || countryObj?.name);

  const monitoringEvents = monitoringEventSummary({
    pepRequired: monitoring?.pep_and_sanction_required,
    adverseRequired: monitoring?.adverse_media_required,
    isEntity,
    isLexisNexis2,
  });

  const [showSuccess, setShowSuccess] = useState(false);

  const handleCloseSuccess = () => {
    setShowSuccess(false);
    navigate(`/profile/${profileId}`);
  };

  const handleCancel = () => {
    navigate(`/profile/${profileId}`);
  };

  const handleConfirm = () => {
    if (!monitoring || doubleClick) return;

    setDoubleClick(true);

    const payloadCommon = {
      pep_and_sanction_required: isYes(monitoring.pep_and_sanction_required),
      adverse_media_required: isYes(monitoring.adverse_media_required),
      external_reference:
        externalReference?.trim() || profile?.external_reference || null,
      profile: profileId,
      primary_division: (primaryDivision as number) || undefined,
      primary_team: (primaryTeam as number) || undefined,
    };

    if (isEntity) {
      const date_of_incorporation = profile?.date_of_incorporation
        ? DateTime.fromISO(profile.date_of_incorporation).toFormat("yyyy-MM-dd")
        : null;

      createEntityMonitoring(
        {
          company_name: profile?.name ?? "",
          country: profile?.country?.id ?? null,
          entity_type: profile?.entity_type?.id ?? null,
          registration_number: profile?.registration_number ?? "",
          date_of_incorporation,
          individual_reference: null,
          ...payloadCommon,
        },
        {
          onSuccess: () => {
            setShowSuccess(true);
          },

          onError: (err: any) => {
            setDoubleClick(false);
            showError(
              err,
              "A problem occurred while creating the entity monitoring record.",
            );
          },
        },
      );

      return;
    }

    const dob = profile?.date_of_birth
      ? DateTime.fromISO(profile.date_of_birth).toFormat("yyyy-MM-dd")
      : null;

    createMonitoring(
      {
        gender: profile?.gender ?? "",
        title: profile?.title ?? "",
        first_name: profile?.first_name ?? "",
        middle_name: profile?.middle_name ?? "",
        last_name: profile?.last_name ?? "",
        date_of_birth: dob,
        // email: profile?.email ?? "",
        individual: null,
        individual_reference: null,
        ...payloadCommon,
      },
      {
        onSuccess: () => {
          setShowSuccess(true);
        },

        onError: (err: any) => {
          setDoubleClick(false);
          showError(err, "A problem occurred while creating the individual.");
        },
      },
    );
  };

  return (
    <NewMonitoringRecordFrame step="summary">
      <Box my={2} maxWidth={560} minWidth={520}>
        <H3>Summary</H3>
        <Paragraph>
          Please review the details for this monitoring record.
        </Paragraph>

        {/* Details card */}
        <Box mt={3}>
          <SoftCard>
            <Paragraph fontSize={2} fontWeight={700} mb={2}>
              {displayName}
            </Paragraph>

            {isEntity ? (
              <>
                {entityTypeName && <Muted>{entityTypeName}</Muted>}

                {registrationNumber && (
                  <Paragraph
                    mt={2}
                    mb={0}
                    color={theme.colors.muted}
                    fontWeight={600}
                  >
                    {registrationNumber}
                  </Paragraph>
                )}

                {incorporationDate && <Muted>{incorporationDate}</Muted>}
              </>
            ) : (
              <>
                {dateOfBirth && <Muted>{dateOfBirth}</Muted>}

                {genderLabel && (
                  <Paragraph
                    mt={2}
                    mb={0}
                    color={theme.colors.muted}
                    fontWeight={600}
                  >
                    {genderLabel}
                  </Paragraph>
                )}

                {email && <Muted>{email}</Muted>}
              </>
            )}

            {countryObj?.name ? (
              <Flex alignItems="center" gap="8" mt={2}>
                {!!countryObj?.icon && (
                  <Box backgroundColor="gray.10" borderRadius="50%" p="1">
                    <img
                      src={`data:image/png;base64,${countryObj.icon}`}
                      alt="flag"
                      style={{
                        display: "block",
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                      }}
                    />
                  </Box>
                )}

                <Paragraph mt={0} mb={0} fontWeight={600}>
                  {countryObj.name}
                </Paragraph>
              </Flex>
            ) : null}

            {!hasDetails && <Muted>No further details provided.</Muted>}
          </SoftCard>
        </Box>

        {/* Checks card */}
        <Box mt={3}>
          <SoftCard>
            <Paragraph fontSize={2} fontWeight={700} mb={2}>
              Checks
            </Paragraph>

            {monitoringEvents.map(({ key, label, icon }, index) => (
              <Flex
                gap="12"
                alignItems="center"
                key={key}
                mb={index < monitoringEvents.length - 1 ? 2 : 0}
              >
                <Image src={icon} alt={label} height="auto" mr={1} />
                <Muted>{label}</Muted>
              </Flex>
            ))}

            {monitoringEvents.length === 0 && (
              <Muted>No checks selected.</Muted>
            )}
          </SoftCard>
        </Box>

        {/* Footer buttons */}
        <Flex justifyContent="space-between" alignItems="center" mt={4}>
          <Button
            variant="text"
            onClick={handleCancel}
            isDisabled={doubleClick}
          >
            Cancel
          </Button>

          <Button
            onClick={handleConfirm}
            isDisabled={doubleClick || !monitoring}
          >
            {doubleClick ? "Confirming..." : "Confirm"}
          </Button>
        </Flex>
      </Box>
      <Modal
        isOpen={showSuccess}
        onClose={handleCloseSuccess}
        title="Monitoring record created successfully!"
        content="Thank you for creating a monitoring record for this profile. The results of the initial screening will be available shortly."
        primaryButtonText="Close"
        primaryButtonVariant="primary"
        onModalConfirmClick={handleCloseSuccess}
        width="450px"
        hasIcon={true}
        isSuccessful={true}
      />
    </NewMonitoringRecordFrame>
  );
};
